import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AXIOS_INSTANCE, customInstance } from '../../services/api-client';
import type {
  PayrollConnectorOption,
  PayrollDataSource,
  PayrollDataSourceConfigUpdate,
  PayrollHealth,
  PayrollRun,
  PayrollSourceFile,
} from './types';

const BASE = '/api/payroll-entry';

export const payrollEntryKeys = {
  all: ['payroll-entry'] as const,
  run: (period: string) => [...payrollEntryKeys.all, 'run', period] as const,
  sourceFiles: () => [...payrollEntryKeys.all, 'source-files'] as const,
  config: () => [...payrollEntryKeys.all, 'config'] as const,
  connectors: () => [...payrollEntryKeys.all, 'available-connectors'] as const,
};

export function usePayrollRun(period: string) {
  return useQuery<PayrollRun>({
    queryKey: payrollEntryKeys.run(period),
    queryFn: () => customInstance({
      url: `${BASE}/run`,
      method: 'GET',
      params: { period },
    }),
  });
}

export function usePayrollSourceFiles() {
  return useQuery<PayrollSourceFile[]>({
    queryKey: payrollEntryKeys.sourceFiles(),
    queryFn: () => customInstance({ url: `${BASE}/source-files`, method: 'GET' }),
  });
}

export function usePayrollDataSourceConfig() {
  return useQuery<PayrollDataSource>({
    queryKey: payrollEntryKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  });
}

export function usePayrollAvailableConnectors() {
  return useQuery<PayrollConnectorOption[]>({
    queryKey: payrollEntryKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  });
}

export function useSetPayrollDataSource() {
  const queryClient = useQueryClient();
  return useMutation<PayrollDataSource, Error, PayrollDataSourceConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: payrollEntryKeys.all }),
  });
}

export function useClearPayrollDataSource() {
  const queryClient = useQueryClient();
  return useMutation<PayrollDataSource, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: payrollEntryKeys.all }),
  });
}

export function useTestPayrollConnection() {
  return useMutation<PayrollHealth, Error, PayrollDataSourceConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  });
}

export async function downloadPayrollSourceFile(file: PayrollSourceFile) {
  const response = await AXIOS_INSTANCE.request<Blob>({
    url: `${BASE}/source-files/${file.id}`,
    method: 'GET',
    responseType: 'blob',
  });

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}
